import { reportFormAtom } from 'stores/report-store/report-store';
import { useAtom } from 'jotai';
import React, { createContext, useContext } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

export type Client = {
  id: number;
  clientName: string;
  clientSurname: string;
  investmentType: string;
};

type ReportFormData = {
  clients: Client[];
  period: { start: Date | Date[]; end: Date | Date[] };
  notes: string;
  reportType: 'investmentReport' | 'investmentOverview';
  selectedOptions: string[];
};

const defaultFormData: ReportFormData = {
  clients: [],
  period: { start: new Date(), end: new Date() },
  notes: '',
  reportType: 'investmentReport',
  selectedOptions: []
};

const isStepValid = (step: number, formData: ReportFormData) => {
  switch (step) {
    case 1:
      return formData.clients.length > 0;

    case 2: {
      const startDate = Array.isArray(formData.period.start)
        ? formData.period.start[0]
        : formData.period.start;
      const endDate = Array.isArray(formData.period.end)
        ? formData.period.end[0]
        : formData.period.end;
      return (
        startDate !== undefined &&
        endDate !== undefined &&
        !isNaN(startDate.getTime()) &&
        !isNaN(endDate.getTime())
      );
    }

    case 3:
      return true;

    default:
      return false;
  }
};

type ReportFormContextType = {
  formData: ReportFormData;
  updateFormData: (updates: Partial<ReportFormData>) => void;
  toggleClient: (client: Client) => void;
  isStepValid: (step: number) => boolean;
  formMethods: UseFormReturn<ReportFormData>;
  toggleOption: (option: string) => void;
  clearSelections: () => void;
};

const ReportFormContext = createContext<ReportFormContextType | undefined>(undefined);

export const useReportForm = () => {
  const context = useContext(ReportFormContext);
  if (!context) {
    throw new Error('useReportForm must be used within a ReportFormProvider');
  }
  return context;
};

export const ReportFormProvider = ({ children }: { children: React.ReactNode }) => {
  const formMethods = useForm<ReportFormData>({
    defaultValues: defaultFormData
  });
  const [formData, setFormData] = useAtom(reportFormAtom);

  const toggleOption = (option: string) => {
    setFormData((prev) => {
      const isSelected = prev.selectedOptions.includes(option);
      const updatedOptions = isSelected
        ? prev.selectedOptions.filter((opt) => opt !== option)
        : [...prev.selectedOptions, option];

      return { ...prev, selectedOptions: updatedOptions };
    });
  };

  const clearSelections = () => {
    setFormData((prev) => ({
      ...prev,
      selectedOptions: []
    }));
  };

  const updateFormData = (updates: Partial<ReportFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const toggleClient = (client: Client) => {
    setFormData((prev) => {
      const isSelected = prev.clients.some((c) => c.id === client.id);
      return {
        ...prev,
        clients: isSelected
          ? prev.clients.filter((c) => c.id !== client.id)
          : [...prev.clients, client]
      };
    });
  };

  const contextValue: ReportFormContextType = {
    formData,
    updateFormData,
    toggleClient,
    toggleOption,
    clearSelections,
    isStepValid: (step) => isStepValid(step, formData),
    formMethods
  };

  return <ReportFormContext.Provider value={contextValue}>{children}</ReportFormContext.Provider>;
};
